import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Breadcrumbs, BreadcrumbItem } from '@heroui/react';
import { ROUTES } from '@/shared/config';

interface BreadcrumbSegment {
  label: string;
  href?: string;
}

export const DynamicBreadcrumbs: React.FC = () => {
  const location = useLocation();
  const params = useParams();

  const getBreadcrumbs = (): BreadcrumbSegment[] => {
    const path = location.pathname;
    const segments: BreadcrumbSegment[] = [];

    // Always add home/dashboard as first item
    segments.push({
      label: 'Dashboard',
      href: ROUTES.DASHBOARD,
    });

    // Handle specific routes
    if (path === ROUTES.DASHBOARD) {
      // Already handled - just show Dashboard
      return segments;
    } else if (path === ROUTES.PHONEBOARDS) {
      segments.push({
        label: 'Phoneboards',
      });
    } else if (path.startsWith('/phoneboards/')) {
      segments.push({
        label: 'Phoneboards',
        href: ROUTES.PHONEBOARDS,
      });

      if (params.id) {
        segments.push({
          label: `Phoneboard ${params.id}`,
        });
      }
    } else if (path === ROUTES.ACCOUNTS) {
      segments.push({
        label: 'Account Management',
      });
    } else if (path === ROUTES.SNS_ACCOUNTS) {
      segments.push({
        label: 'SNS Account Management',
      });
    } else if (path === ROUTES.TASKS) {
      segments.push({
        label: 'Task Management',
      });
    } else if (path === ROUTES.TASK_HISTORY) {
      segments.push({
        label: 'Task History',
      });
    } else if (path === ROUTES.NOTIFICATIONS) {
      segments.push({
        label: 'Notifications',
      });
    } else {
      // Generic fallback - split path and capitalize
      const pathParts = path.split('/').filter(Boolean);
      pathParts.forEach((part, index) => {
        // Skip first part (already added as Dashboard)
        if (index === 0) return;

        const isLast = index === pathParts.length - 1;
        const label = part
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

        segments.push({
          label,
          href: isLast ? undefined : `/${pathParts.slice(0, index + 1).join('/')}`,
        });
      });
    }

    return segments;
  };

  const breadcrumbs = getBreadcrumbs();

  // Don't show breadcrumbs if only dashboard
  if (breadcrumbs.length <= 0) {
    return null;
  }

  return (
    <Breadcrumbs
      size="lg"
      classNames={{
        list: "gap-2",
      }}
    >
      {breadcrumbs.map((crumb, index) => {
        const isLast = index === breadcrumbs.length - 1;

        return (
          <BreadcrumbItem
            key={`${crumb.label}-${index}`}
            href={crumb.href}
            isCurrent={isLast}
          >
            {crumb.label}
          </BreadcrumbItem>
        );
      })}
    </Breadcrumbs>
  );
};

DynamicBreadcrumbs.displayName = 'DynamicBreadcrumbs';
